import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Events.css';

const Registrations = () => {
  const [registrations, setRegistrations] = useState([]);
  const [events, setEvents] = useState([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [eventFilter, setEventFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const fetchRegistrations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/events');
      setEvents(response.data || []);
      setTotalEvents(response.data.length || 0);
      
      // Flatten all registrations from all events
      const allRegistrations = [];
      
      for (const event of response.data) {
        // Fetch event details (includes registrations with answers)
        const eventDetailResponse = await api.get(`/events/${event.EventId}`);
        const eventRegistrations = eventDetailResponse.data.registrations.map(reg => ({
          ...reg,
          EventTitle: event.Title,
          EventDate: event.EventDate,
          EventId: event.EventId
        }));
        const pendingInvites = (eventDetailResponse.data.pendingInvites || []).map((invite, index) => ({
          RegistrationId: `pending-${event.EventId}-${invite.ContactId || invite.Email || index}`,
          EventId: event.EventId,
          EventTitle: event.Title,
          EventDate: event.EventDate,
          ContactId: invite.ContactId || null,
          Email: invite.Email || '',
          FirstName: invite.FirstName || '',
          LastName: invite.LastName || '',
          Company: invite.Company || '',
          Phone: invite.Phone || '',
          Status: 'pending',
          RegistrationDate: invite.InvitedAt || null,
          IsPending: true
        }));
        allRegistrations.push(...eventRegistrations);
        allRegistrations.push(...pendingInvites);
      }
      
      // Sort by registration date (newest first)
      allRegistrations.sort((a, b) => 
        new Date(b.RegistrationDate) - new Date(a.RegistrationDate)
      );
      
      setRegistrations(allRegistrations);
      setLoading(false);
    } catch (err) {
      setError('Failed to load registrations');
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case 'registered': return 'status-badge status-registered';
      case 'confirmed': return 'status-badge status-registered';
      case 'attended': return 'status-badge status-attended';
      case 'waitlisted': return 'status-badge status-waitlisted';
      case 'declined': return 'status-badge status-declined';
      case 'cancelled': return 'status-badge status-cancelled';
      case 'no-show': return 'status-badge status-no-show';
      case 'pending': return 'status-badge status-waitlisted';
      default: return 'status-badge';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredRegistrations = registrations.filter(reg => {
    const matchesSearch = 
      reg.Email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (reg.FirstName && reg.FirstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (reg.LastName && reg.LastName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (reg.Company && reg.Company.toLowerCase().includes(searchTerm.toLowerCase())) ||
      reg.EventTitle.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || reg.Status.toLowerCase() === statusFilter.toLowerCase();
    const matchesEvent = eventFilter === 'all' || String(reg.EventId) === eventFilter;
    
    return matchesSearch && matchesStatus && matchesEvent;
  });

  const eventOptions = (events || []).map(event => ({
    id: event.EventId,
    title: event.Title
  })).sort((a, b) => a.title.localeCompare(b.title));

  const getStatusCounts = () => {
    return {
      all: registrations.length,
      registered: registrations.filter(r => r.Status.toLowerCase() === 'registered').length,
      confirmed: registrations.filter(r => r.Status.toLowerCase() === 'confirmed').length,
      attended: registrations.filter(r => r.Status.toLowerCase() === 'attended').length,
      waitlisted: registrations.filter(r => r.Status.toLowerCase() === 'waitlisted').length,
      declined: registrations.filter(r => r.Status.toLowerCase() === 'declined').length,
      cancelled: registrations.filter(r => r.Status.toLowerCase() === 'cancelled').length,
      'no-show': registrations.filter(r => r.Status.toLowerCase() === 'no-show').length,
      pending: registrations.filter(r => r.Status.toLowerCase() === 'pending').length
    };
  };

  const statusCounts = getStatusCounts();

  if (loading) return <div className="loading">Loading registrations...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="events-container">
      <div className="events-header">
        <h1>All Event Registrations</h1>
        <div className="events-stats">
          <div className="stat-card">
            <div className="stat-value">{totalEvents}</div>
            <div className="stat-label">Total Events</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{statusCounts.all}</div>
            <div className="stat-label">Total Registrations</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{statusCounts.registered}</div>
            <div className="stat-label">Registered</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{statusCounts.attended}</div>
            <div className="stat-label">Attended</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{statusCounts.cancelled}</div>
            <div className="stat-label">Cancelled</div>
          </div>
        </div>
      </div>

      <div className="events-filters">
        <input
          type="text"
          placeholder="Search by name, email, company, or event..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select 
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value)}
          className="status-filter"
        >
          <option value="all">All Statuses ({statusCounts.all})</option>
          <option value="registered">Registered ({statusCounts.registered})</option>
          <option value="confirmed">Confirmed ({statusCounts.confirmed})</option>
          <option value="attended">Attended ({statusCounts.attended})</option>
          <option value="waitlisted">Waitlisted ({statusCounts.waitlisted})</option>
          <option value="pending">Pending RSVP ({statusCounts.pending})</option>
          <option value="declined">Declined ({statusCounts.declined})</option>
          <option value="cancelled">Cancelled ({statusCounts.cancelled})</option>
          <option value="no-show">No Show ({statusCounts['no-show']})</option>
        </select>
        <select
          value={eventFilter}
          onChange={(e) => setEventFilter(e.target.value)}
          className="status-filter"
        >
          <option value="all">All Events</option>
          {eventOptions.map(event => (
            <option key={event.id} value={String(event.id)}>{event.title}</option>
          ))}
        </select>
      </div>

      {filteredRegistrations.length === 0 ? (
        <div className="empty-state">
          <p>No registrations found</p>
        </div>
      ) : (
        <div className="registrations-table-container">
          <table className="registrations-table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Event Date</th>
                <th>Name</th>
                <th>Email</th>
                <th>Company</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Registered</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRegistrations.map((reg) => (
                <tr key={reg.RegistrationId}>
                  <td>
                    <button 
                      onClick={() => navigate(`/events/${reg.EventId}`)}
                      className="event-link"
                      style={{ background: 'none', border: 'none', color: '#0078d4', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      {reg.EventTitle}
                    </button>
                  </td>
                  <td>{formatDate(reg.EventDate)}</td>
                  <td>{reg.FirstName} {reg.LastName}</td>
                  <td>{reg.Email}</td>
                  <td>{reg.Company || '-'}</td>
                  <td>{reg.Phone || '-'}</td>
                  <td>
                    <span className={getStatusBadgeClass(reg.Status)}>
                      {reg.Status}
                    </span>
                  </td>
                  <td>{reg.RegistrationDate ? `${reg.IsPending ? 'Invited' : 'Registered'} ${formatDate(reg.RegistrationDate)}` : '-'}</td>
                  <td>
                    <button
                      onClick={() => navigate(`/events/${reg.EventId}`)}
                      className="btn btn-sm btn-secondary"
                    >
                      View Event
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Registrations;
